import React, { createContext, useEffect, useMemo, useState } from "react";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";

export const CartContext = createContext(null);

const readStoredValue = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const saveStoredValue = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing; in-memory state still works.
  }
};

const productIds = new Set(productData.map((product) => product.id));

const sanitizeCart = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, item) => {
    const id = Number(item?.id);
    const quantity = Math.min(99, Math.max(1, Number(item?.quantity) || 1));
    if (!productIds.has(id)) return items;
    return [...items, {
      id,
      quantity,
      size: typeof item.size === "string" ? item.size : "",
      color: typeof item.color === "string" ? item.color : "",
    }];
  }, []);
};

const sanitizeWishlist = (value) => {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map(Number).filter((id) => productIds.has(id)))];
};

const sanitizeDiscount = (value) =>
  typeof value === "string" && value === siteConfig.commerce.discountCode ? value : "";

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    sanitizeCart(readStoredValue(siteConfig.storage.cart, []))
  );
  const [wishlist, setWishlist] = useState(() =>
    sanitizeWishlist(readStoredValue(siteConfig.storage.wishlist, []))
  );
  const [appliedDiscountCode, setAppliedDiscountCode] = useState(() =>
    sanitizeDiscount(readStoredValue(siteConfig.storage.discount, ""))
  );

  useEffect(() => saveStoredValue(siteConfig.storage.cart, cartItems), [cartItems]);
  useEffect(() => saveStoredValue(siteConfig.storage.wishlist, wishlist), [wishlist]);
  useEffect(
    () => saveStoredValue(siteConfig.storage.discount, appliedDiscountCode),
    [appliedDiscountCode]
  );

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) => {
      const itemIndex = items.findIndex(
        (item) => item.id === productId && item.size === size && item.color === color
      );

      if (itemIndex === -1) {
        return [...items, { id: productId, size, color, quantity: safeQuantity }];
      }

      return items.map((item, index) =>
        index === itemIndex ? { ...item, quantity: item.quantity + safeQuantity } : item
      );
    });
  };

  const removeFromCart = (productId, size = "", color = "") => {
    setCartItems((items) =>
      items.filter(
        (item) => !(item.id === productId && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) =>
      items.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: safeQuantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setAppliedDiscountCode("");
  };

  const applyDiscountCode = (code) => {
    const normalizedCode = code.trim().toUpperCase();
    const isValid = normalizedCode === siteConfig.commerce.discountCode;
    setAppliedDiscountCode(isValid ? normalizedCode : "");
    return isValid;
  };

  const removeDiscountCode = () => setAppliedDiscountCode("");

  const addToWishlist = (productId) =>
    setWishlist((items) => (items.includes(productId) ? items : [...items, productId]));

  const removeFromWishlist = (productId) =>
    setWishlist((items) => items.filter((id) => id !== productId));

  const toggleWishlist = (productId) =>
    setWishlist((items) =>
      items.includes(productId) ? items.filter((id) => id !== productId) : [...items, productId]
    );

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const value = {
    cartItems,
    wishlist,
    cartCount,
    appliedDiscountCode,
    addToCart,
    addCart: addToCart,
    removeFromCart,
    removeCart: removeFromCart,
    updateQuantity,
    clearCart,
    applyDiscountCode,
    removeDiscountCode,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist: (productId) => wishlist.includes(productId),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
